/**
 * API Client for Admin Dashboard
 * Axios-based client with authentication and error handling
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  ApiResponse,
  ListResponse,
  Submission,
  File,
  User,
  SecurityEvent,
  ApiKey,
  ExportJob,
  DashboardStats,
  Pagination,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/admin';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Send cookies for session auth
    });

    // Request interceptor for auth
    this.client.interceptors.request.use(
      (config) => {
        // Session is handled via httpOnly cookies
        // No need to manually add Authorization header
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Unauthorized - redirect to login
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.client.get<ApiResponse<{ stats: DashboardStats }>>(
      '/dashboard'
    );
    return response.data.data!.stats;
  }

  // Submissions
  async listSubmissions(params?: {
    page?: number;
    limit?: number;
    form_origin?: string;
    status?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<ListResponse<Submission>> {
    const response = await this.client.get<
      ApiResponse<{ submissions: Submission[]; pagination: Pagination }>
    >('/submissions', { params });
    return {
      items: response.data.data!.submissions,
      pagination: response.data.data!.pagination,
    };
  }

  async getSubmission(id: string): Promise<{ submission: Submission; files: File[] }> {
    const response = await this.client.get<
      ApiResponse<{ submission: Submission; files: File[] }>
    >(`/submissions/${id}`);
    return response.data.data!;
  }

  async updateSubmission(id: string, data: Partial<Submission>): Promise<Submission> {
    const response = await this.client.put<ApiResponse<{ submission: Submission }>>(
      `/submissions/${id}`,
      data
    );
    return response.data.data!.submission;
  }

  async deleteSubmission(id: string): Promise<void> {
    await this.client.delete(`/submissions/${id}`);
  }

  async deleteSubmissions(ids: string[]): Promise<void> {
    await this.client.post('/submissions/bulk-delete', { ids });
  }

  // Files
  async listFiles(params?: {
    page?: number;
    limit?: number;
    security_status?: string;
  }): Promise<ListResponse<File>> {
    const response = await this.client.get<
      ApiResponse<{ files: File[]; pagination: Pagination }>
    >('/files', { params });
    return {
      items: response.data.data!.files,
      pagination: response.data.data!.pagination,
    };
  }

  async getFile(id: string): Promise<File> {
    const response = await this.client.get<ApiResponse<{ file: File }>>(`/files/${id}`);
    return response.data.data!.file;
  }

  async downloadFile(submissionId: string, fileId: string): Promise<Blob> {
    const response = await this.client.get(
      `/submissions/${submissionId}/files/${fileId}`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  async deleteFile(id: string): Promise<void> {
    await this.client.delete(`/files/${id}`);
  }

  // Security
  async listSecurityEvents(params?: {
    page?: number;
    limit?: number;
    severity?: string;
    detection_layer?: string;
  }): Promise<ListResponse<SecurityEvent>> {
    const response = await this.client.get<
      ApiResponse<{ events: SecurityEvent[]; pagination: Pagination }>
    >('/security/events', { params });
    return {
      items: response.data.data!.events,
      pagination: response.data.data!.pagination,
    };
  }

  async getSecurityStats(days?: number): Promise<any> {
    const response = await this.client.get<ApiResponse<{ stats: any }>>(
      '/security/stats',
      { params: { days } }
    );
    return response.data.data!.stats;
  }

  async getSecurityTimeline(days: number): Promise<any[]> {
    const response = await this.client.get<ApiResponse<{ timeline: any[] }>>(
      '/security/timeline',
      { params: { days } }
    );
    return response.data.data!.timeline;
  }

  async getThreatBreakdown(): Promise<any> {
    const response = await this.client.get<ApiResponse<{ breakdown: any }>>(
      '/security/breakdown'
    );
    return response.data.data!.breakdown;
  }

  async listQuarantinedFiles(): Promise<File[]> {
    const response = await this.client.get<ApiResponse<{ files: File[] }>>(
      '/security/quarantine'
    );
    return response.data.data!.files;
  }

  async restoreQuarantinedFile(fileId: string): Promise<void> {
    await this.client.post(`/security/quarantine/${fileId}/restore`);
  }

  async deleteQuarantinedFile(fileId: string): Promise<void> {
    await this.client.delete(`/security/quarantine/${fileId}`);
  }

  // Users (super_admin only)
  async listUsers(): Promise<User[]> {
    const response = await this.client.get<ApiResponse<{ users: User[] }>>('/users');
    return response.data.data!.users;
  }

  async createUser(data: { email: string; name: string; role: string }): Promise<User> {
    const response = await this.client.post<ApiResponse<{ user: User }>>('/users', data);
    return response.data.data!.user;
  }

  async updateUser(id: string, data: Partial<User>): Promise<User> {
    const response = await this.client.put<ApiResponse<{ user: User }>>(`/users/${id}`, data);
    return response.data.data!.user;
  }

  async deleteUser(id: string): Promise<void> {
    await this.client.delete(`/users/${id}`);
  }

  // API Keys
  async listApiKeys(): Promise<ApiKey[]> {
    const response = await this.client.get<ApiResponse<{ keys: ApiKey[] }>>('/api-keys');
    return response.data.data!.keys;
  }

  async createApiKey(data: {
    name: string;
    form_origins: string[];
    permissions: string[];
    expires_in_days?: number;
  }): Promise<{ key: string; id: string }> {
    const response = await this.client.post<ApiResponse<{ key: string; id: string }>>(
      '/api-keys',
      data
    );
    return response.data.data!;
  }

  async updateApiKey(id: string, data: Partial<ApiKey>): Promise<ApiKey> {
    const response = await this.client.put<ApiResponse<{ key: ApiKey }>>(
      `/api-keys/${id}`,
      data
    );
    return response.data.data!.key;
  }

  async revokeApiKey(id: string): Promise<void> {
    await this.client.post(`/api-keys/${id}/revoke`);
  }

  // Configuration
  async getConfig(): Promise<any> {
    const response = await this.client.get<ApiResponse<{ config: any }>>('/config');
    return response.data.data!.config;
  }

  async updateConfig(data: any): Promise<any> {
    const response = await this.client.put<ApiResponse<{ config: any }>>('/config', data);
    return response.data.data!.config;
  }

  async getConfigChangeLog(): Promise<any[]> {
    const response = await this.client.get<ApiResponse<{ changes: any[] }>>('/config/changes');
    return response.data.data!.changes;
  }

  async testWebhook(url: string): Promise<void> {
    await this.client.post('/config/test-webhook', { url });
  }

  // Exports
  async listExports(): Promise<ExportJob[]> {
    const response = await this.client.get<ApiResponse<{ jobs: ExportJob[] }>>('/exports');
    return response.data.data!.jobs;
  }

  async createExport(data: {
    format: 'csv' | 'json' | 'zip';
    filters?: any;
  }): Promise<ExportJob> {
    const response = await this.client.post<ApiResponse<{ jobId: string; status: string }>>(
      '/exports',
      data
    );
    return {
      id: response.data.data!.jobId,
      format: data.format,
      status: response.data.data!.status as any,
      filters: data.filters,
      created_at: new Date().toISOString(),
    };
  }

  async getExportStatus(jobId: string): Promise<ExportJob> {
    const response = await this.client.get<ApiResponse<{ job: ExportJob }>>(
      `/exports/${jobId}`
    );
    return response.data.data!.job;
  }

  // Auth
  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<ApiResponse<{ user: User }>>('/auth/me');
    return response.data.data!.user;
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout');
  }
}

export const apiClient = new ApiClient();
